import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MobileMenu } from '../ui';
import Footer from './Footer';

// Icons
import { 
  FiHome, FiFileText, FiBriefcase, FiClipboard, 
  FiSettings, FiLogOut, FiMenu, FiUser, FiBell,
  FiMail, FiShield, FiCpu, FiAward, FiUsers, FiBookmark,
  FiMessageSquare, FiBarChart2, FiMic, FiUserCheck
} from 'react-icons/fi';

// Import MembershipIndicator
import MembershipIndicator from '../membership/MembershipIndicator';
import tourService from '../../services/tourService';
import QuickAssistant from './QuickAssistant';

const Layout = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('resumemo-sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserMenu = () => setUserMenuOpen(!userMenuOpen);
  const closeSidebar = () => setSidebarOpen(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Save sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('resumemo-sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);
  
  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Regular user navigation links
  const userNavLinks = [
    { path: '/', label: 'Dashboard', icon: <FiHome size={20} /> },
    { path: '/career-direction', label: 'Career Direction', icon: <FiCpu size={20} /> },
    { path: '/resumes', label: 'Resumes', icon: <FiFileText size={20} />, pathMatch: path => path.startsWith('/resumes') },
    { path: '/jobs/saved', label: 'Saved Jobs', icon: <FiBookmark size={20} />, pathMatch: path => path.startsWith('/jobs/saved') },
    { path: '/applications', label: 'Applications', icon: <FiClipboard size={20} /> },
    { path: '/jobs/mock-interview', label: 'Mock Interview', icon: <FiMic size={20} />, pathMatch: path => path.startsWith('/jobs/mock-interview') },
    { path: '/membership', label: 'Membership', icon: <FiAward size={20} /> },
    { path: '/contact', label: 'Contact', icon: <FiMail size={20} /> },
    { path: '/settings', label: 'Settings', icon: <FiSettings size={20} /> },
  ];
  
  // Admin navigation links
  const adminNavLinks = [
    { path: '/admin', label: 'Admin Dashboard', icon: <FiBarChart2 size={20} /> },
    { path: '/admin/users', label: 'User Management', icon: <FiUsers size={20} /> },
    { path: '/admin/tickets', label: 'Ticket Management', icon: <FiMessageSquare size={20} /> },
  ];
  
  // Mentor navigation links
  const mentorNavLinks = [
    { path: '/mentor/dashboard', label: 'Mentor Dashboard', icon: <FiBarChart2 size={20} /> },
    { path: '/mentor/mentees', label: 'My Mentees', icon: <FiUsers size={20} /> },
    { path: '/mentor/job-assignments', label: 'Job Assignments', icon: <FiBriefcase size={20} /> },
    { path: '/mentor/tickets', label: 'Mentee Tickets', icon: <FiMessageSquare size={20} /> },
  ];
  
  // Combine links based on user role
  let navLinks = userNavLinks;
  
  if (currentUser?.is_admin) {
    navLinks = [...userNavLinks, { divider: true }, ...adminNavLinks];
  } else if (currentUser?.membership_type === 'mentor') {
    navLinks = [...userNavLinks, { divider: true }, ...mentorNavLinks];
  }
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col" style={{ width: sidebarCollapsed ? 64 : 256, transition: 'width 0.2s ease' }}>
          <div className="flex flex-col flex-grow overflow-y-auto overflow-x-hidden bg-white border-r">
            <div className="flex items-center flex-shrink-0 px-3 py-3 gap-2 border-b border-gray-100" style={{ minHeight: 48 }}>
              {!sidebarCollapsed && (
                <>
                  <img src="/logo.png" alt="" className="h-7 w-auto flex-shrink-0" aria-hidden="true" />
                  <h1 className="text-lg font-semibold text-primary-600 font-serif whitespace-nowrap flex-1">Resumemo</h1>
                </>
              )}
              <button
                type="button"
                onClick={() => setSidebarCollapsed(c => !c)}
                className="flex items-center justify-center p-1.5 rounded-md text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors flex-shrink-0"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <FiMenu size={18} />
              </button>
            </div>
            <div className="mt-2 flex-grow flex flex-col">
              <nav className="flex-1 px-2 space-y-1">
              {navLinks.map((link, index) => {
  const isActive = !link.divider && (location.pathname === link.path || (link.pathMatch && link.pathMatch(location.pathname)));
  return link.divider ? (
    <div key={`divider-${index}`} className="border-t border-gray-200 my-3"></div>
  ) : (
    <Link
      key={link.path}
      id={`${String(link.label ?? '').toLowerCase().replace(/\s+/g, '-')}-nav`}
      to={link.path}
      title={sidebarCollapsed ? link.label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={`
        group flex items-center px-2 py-2 text-sm font-medium rounded-md
        ${isActive
          ? 'bg-primary-50 text-primary-700 font-bold border-l-4 border-primary-700'
          : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'}
      `}
      style={sidebarCollapsed ? { justifyContent: 'center' } : undefined}
    >
      <span className={`${sidebarCollapsed ? '' : 'mr-3'} flex-shrink-0 ${isActive ? 'text-primary-700' : 'text-secondary-500 group-hover:text-secondary-700'}`}>
        {link.icon}
      </span>
      {!sidebarCollapsed && <span className="whitespace-nowrap">{link.label}</span>}
    </Link>
  );
})}
              </nav>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <MobileMenu id="mobile-sidebar" isOpen={sidebarOpen} onClose={closeSidebar}>
        <div className="flex items-center flex-shrink-0 px-4 mb-5 gap-2">
          <img src="/logo.png" alt="" className="h-8 w-auto" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-primary-600 font-serif">Resumemo</h1>
        </div>
        <nav className="space-y-1">
          {navLinks.map((link, index) => {
            const isActive = !link.divider && (location.pathname === link.path || (link.pathMatch && link.pathMatch(location.pathname)));
            return link.divider ? (
              <div key={`divider-${index}`} className="border-t border-gray-200 my-3"></div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                aria-current={isActive ? 'page' : undefined}
                className={`
                  group flex items-center px-2 py-2 text-base font-medium rounded-md
                  ${isActive
                    ? 'bg-primary-50 text-primary-700 font-bold border-l-4 border-primary-700'
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'}
                `}
                onClick={closeSidebar}
              >
                <span className={`mr-4 ${isActive ? 'text-primary-700' : 'text-secondary-500 group-hover:text-secondary-700'}`}>
                  {link.icon}
                </span>
                {link.label}
              </Link>
            );
          })}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-2 py-2 text-base font-medium text-red-600 rounded-md hover:bg-red-50 hover:text-red-700"
            >
              <FiLogOut className="mr-4" />
              Logout
            </button>
          </div>
        </nav>
      </MobileMenu>
      
      {/* Main content */}
      <div className="flex flex-col flex-1 w-0 overflow-hidden">
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-secondary-400 hover:text-secondary-500 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                onClick={toggleSidebar}
                aria-expanded={sidebarOpen}
                aria-controls="mobile-sidebar"
                aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                <FiMenu className="h-6 w-6" aria-hidden="true" />
              </button>
              
              {/* Page title in nav bar (not h1 - main content has the page h1) */}
              <div className="md:hidden ml-2">
                <span className="text-lg font-medium text-secondary-900" aria-hidden="true">
                  {navLinks.find(link => {
                    // Exact match takes precedence
                    if (location.pathname === link.path) return true;
                    // Then try pathMatch if available
                    return link.pathMatch && link.pathMatch(location.pathname);
                  })?.label || 'Dashboard'}
                </span>
              </div>
              
              <div className="hidden md:block ml-4">
                <span className="text-xl font-medium text-secondary-900" aria-hidden="true">
                  {navLinks.find(link => {
                    if (location.pathname === link.path) return true;
                    return link.pathMatch && link.pathMatch(location.pathname);
                  })?.label || 'Dashboard'}
                </span>
              </div>
            </div>
            
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notification button */}
              <button
                type="button"
                className="p-1 rounded-full text-secondary-400 hover:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <span className="sr-only">View notifications</span>
                <FiBell className="h-6 w-6" />
              </button>
              
              {/* Membership indicator */}
              <div className="ml-3">
                <MembershipIndicator />
              </div>

              {/* Profile dropdown */}
              <div className="ml-3 relative">
                <div>
                  <button
                    type="button"
                    className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    id="user-menu-button"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                    onClick={toggleUserMenu}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-secondary-200 flex items-center justify-center">
                      <FiUser className="h-5 w-5 text-secondary-500" />
                    </div>
                  </button>
                </div>
                
                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div 
                    className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                    tabIndex="-1"
                  >
                    <div className="px-4 py-2 text-sm text-secondary-700 border-b">
                      {currentUser?.full_name || 'User'}
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                      role="menuitem"
                    >
                      <FiUserCheck className="mr-2 h-4 w-4" />
                      Professional Profile
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      role="menuitem"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="min-h-full">
            <div className="min-h-full max-w-full mx-auto px-2 sm:px-3 md:px-4">
              {children}
            </div>
          </div>
        </main>
        <Footer />
        <QuickAssistant />
      </div>
    </div>
  );
};

export default Layout;